import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router';
import {
    ActiveTabInfo,
    BROWSER_ACTION_CAPABILITY,
    BrowserActionName,
    BrowserActionResultReply,
    BrowserReadReply,
    BrowserTabSummary,
    dataUrlToFile,
    ExtensionInfo,
    ExtensionRequestTimeoutError,
    extendExtensionRequestDeadline,
    getActiveTabInfo,
    getExtensionInfo,
    postCaptureRequest,
    postEmbedReady,
    postMarkdownRequest,
    postNavigateRequest,
    postVoiceActivityNote,
    requestBrowserAction,
    requestBrowserNavigate,
    requestBrowserRead,
    requestBrowserScroll,
    requestBrowserTabs,
    subscribeToActiveTabInfo,
    subscribeToBrowserActionPending,
    subscribeToExtensionInfo,
    waitForComposerAttachment
} from '../lib/extensionEmbed';
import {dispatchCustomAttachComposerFiles} from '../lib/customAttachComposerFiles';
import {useCreateAuthenticatedFetch} from '../api/useCreateAuthenticatedFetch';
import {describeScreenshot} from '../api/voice/describeScreenshot';
import {downscaleImageDataUrl} from '../lib/downscaleImageDataUrl';
import unknownErrorToString from '../lib/unknownErrorToString';
import {
    useVoiceAction,
    useVoicePageFact,
    VoiceActionResult
} from './VoiceControlSurfaceContext';
import {
    confirmParameter,
    createVoiceConfirmationGate
} from './voiceConfirmation';
import {sliceSpokenText} from './voiceMessageText';
import {waitForUiSurfaceToSettle} from './voiceUiTiming';

/**
 * Voice actions for the web page beside the panel when Theta Assist runs inside
 * the Chrome extension. Each action posts a request over the side-panel bridge
 * and awaits the reply inline, so the realtime model gets the observation as a
 * normal tool result. Reading reuses the extension's `browser_read` verbs, which
 * every shipped build understands; navigation, tabs, and scrolling need the
 * newer request types and are offered only when the installed build advertises
 * the matching capability in `extension_hello`.
 *
 * Mounted (globally) only when the SPA is embedded in the extension.
 */

// Mirrors the `kind`-tagged BrowserReadValue union in the extension's
// browserRead.ts, loosely typed: only the fields read here are named.
interface BrowserReadValue {
    kind?: string;
    text?: string;
    content?: string;
    truncated?: boolean;
    elements?: {tag?: string; text?: string; href?: string}[];
    data?: unknown;
    rows?: string[][];
}

interface ReadOutcome {
    ok: boolean;
    value?: BrowserReadValue;
    error?: string;
    // The extension answered on a document/capture channel rather than with
    // inline text (a SharePoint file, for example).
    documentReply?: boolean;
}

interface FindMatch {
    text?: string;
    visible?: boolean;
}

const getStringArg = (args: Record<string, unknown>, name: string): string => {
    const value = args[name];
    return typeof value === 'string' ? value.trim() : '';
};

const getIntegerArg = (
    args: Record<string, unknown>,
    name: string
): number | undefined => {
    const value = args[name];
    const parsed =
        typeof value === 'number'
            ? value
            : typeof value === 'string'
              ? Number.parseInt(value, 10)
              : NaN;
    return Number.isInteger(parsed) ? parsed : undefined;
};

const speak = (
    message: string,
    data?: unknown,
    status: VoiceActionResult['status'] = 'ready'
): VoiceActionResult => ({status, message, speakText: message, data});

const unavailable = (message: string, data?: unknown): VoiceActionResult =>
    speak(message, data, 'unavailable');

const updateNeededMessage =
    'This needs a newer Theta Assist browser extension. Please update the extension and reopen the side panel.';

const noAnswerMessage =
    'The browser extension did not answer. Check that the Theta Assist side panel is still open, or update the extension.';

const mapExtensionError = (error: unknown): VoiceActionResult =>
    error instanceof ExtensionRequestTimeoutError
        ? unavailable(noAnswerMessage, {reason: 'timeout'})
        : unavailable(
              error instanceof Error
                  ? error.message
                  : 'The browser action failed.',
              {reason: 'error'}
          );

const describeReadError = (error: string | undefined): string => {
    if (error && /cannot (read|scroll) this page/i.test(error)) {
        return 'This tab is a browser page I cannot read. Switch to a normal web page and ask again.';
    }
    return error
        ? `The page could not be read: ${error}`
        : 'The page could not be read.';
};

const documentReplyMessage =
    'This tab shows a document rather than a web page. Say "attach this page" to add it to your message so the assistant can read it.';

const hostnameOf = (url: string | undefined): string => {
    if (!url) {
        return 'an unknown site';
    }
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return url;
    }
};

const describeTab = (info: ActiveTabInfo | null): string | null => {
    if (!info) {
        return null;
    }
    if (info.restricted) {
        return 'The browser tab beside the panel is a browser-internal page that cannot be read.';
    }
    if (!info.url && !info.title) {
        return null;
    }
    const title = info.title?.trim();
    return title
        ? `The browser tab shows "${title}" at ${hostnameOf(info.url)}.`
        : `The browser tab shows ${hostnameOf(info.url)}.`;
};

// Add a scheme when the user just said a domain; refuse anything that does not
// look like a web address so the model asks for it rather than guessing.
const normaliseUrl = (raw: string): string | null => {
    const trimmed = raw.trim().replace(/\s+/g, '');
    if (!trimmed) {
        return null;
    }
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
        ? trimmed
        : `https://${trimmed}`;
    try {
        const url = new URL(withScheme);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
            return null;
        }
        const host = url.hostname;
        if (!host.includes('.') && host !== 'localhost') {
            return null;
        }
        return url.toString();
    } catch {
        return null;
    }
};

// The findText tokenizer has no escape syntax, so strip anything that would
// break out of the quoted argument.
const sanitiseSearchText = (text: string): string =>
    text
        .replace(/["'\r\n\\]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const readValueToText = (value: BrowserReadValue | undefined): string => {
    if (!value) {
        return '';
    }
    switch (value.kind) {
        case 'text':
            return (value.text ?? '').trim();
        case 'content':
            return (value.content ?? '').replace(/…\[truncated\]$/, '').trim();
        case 'elements':
            return (value.elements ?? [])
                .map(element => (element.text ?? '').trim())
                .filter(Boolean)
                .join('\n');
        case 'table':
            return (value.rows ?? []).map(row => row.join('; ')).join('\n');
        default:
            return '';
    }
};

const summaryCharacterBudget = 6_000;
const spokenListLimit = 10;

// ---------------------------------------------------------------------------
// Browser actions (snapshot / act / region screenshot). The realtime model has a
// tighter context than text chat, so snapshots are capped lower, and an action
// may sit behind the side panel's Allow/Deny prompt, so its wait is long and is
// stretched further while the extension reports the prompt is open.
// ---------------------------------------------------------------------------

const voiceSnapshotMaxLines = 120;
const voiceSnapshotCharacterBudget = 12_000;
const voiceSnapshotTimeoutMs = 30_000;
const voiceActTimeoutMs = 200_000;
const voiceRegionScreenshotTimeoutMs = 30_000;
// A look is a capture plus one vision completion; allow for a slow model.
const voiceLookTimeoutMs = 45_000;
const pendingConfirmationGraceMs = 5_000;

const voiceActActions = [
    'click',
    'double_click',
    'right_click',
    'hover',
    'type',
    'press',
    'clear',
    'select',
    'check',
    'uncheck',
    'focus',
    'scroll',
    'scroll_into_view',
    'wait_for',
    'describe'
] as const;
type VoiceActAction = (typeof voiceActActions)[number];

// Actions that make sense without an element (the page, or the focused element).
const targetOptionalActions: ReadonlySet<VoiceActAction> = new Set([
    'press',
    'scroll',
    'wait_for'
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const generateVoiceCallId = (prefix: string): string =>
    `${prefix}-${
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    }`;

// The snapshot result carries the model-facing text (see the plan's snapshot
// format) either directly or beside its structured form.
const snapshotText = (result: unknown): string => {
    if (typeof result === 'string') {
        return result;
    }
    if (isRecord(result)) {
        if (typeof result.text === 'string') {
            return result.text;
        }
        if (Array.isArray(result.lines)) {
            return result.lines
                .filter((line): line is string => typeof line === 'string')
                .join('\n');
        }
    }
    return result === undefined ? '' : JSON.stringify(result);
};

// Element lines start with a ref such as `e12` or `f2e12`.
const countSnapshotElements = (text: string): number =>
    (text.match(/^(?:f\d+)?e\d+\s/gm) ?? []).length;

const describeBrowserActionFailure = (
    reply: BrowserActionResultReply,
    fallback: string
): VoiceActionResult => {
    const data = {
        status: reply.status,
        error: reply.error,
        candidates: reply.candidates,
        tabId: reply.tabId,
        url: reply.url,
        title: reply.title
    };
    switch (reply.status) {
        case 'denied':
            return unavailable(
                'The user declined that action in the side panel.',
                data
            );
        case 'stale_ref':
            return unavailable(
                'The page changed; take a new snapshot before acting again.',
                data
            );
        case 'ambiguous': {
            const count = reply.candidates?.length ?? 0;
            return unavailable(
                count > 0
                    ? `${count} elements matched. Pick one by its ref from the candidates, or ask the user which they mean.`
                    : 'Several elements matched. Take a snapshot and act by ref.',
                data
            );
        }
        case 'needs_update':
        case 'unsupported':
            return unavailable(updateNeededMessage, data);
        case 'restricted':
            return unavailable(
                'This tab is a browser page I cannot act on. Switch to a normal web page and ask again.',
                data
            );
        case 'timeout':
            return unavailable(
                reply.error
                    ? `The browser action timed out: ${reply.error}`
                    : 'The browser action timed out.',
                data
            );
        default: {
            const notFound =
                /not found|no element|could not find|no match/i.test(
                    reply.error ?? ''
                );
            return unavailable(
                `${reply.error ? `${fallback}: ${reply.error}` : `${fallback}.`}${
                    notFound
                        ? ' If the user described the element by how it looks or where it sits, use look_at_browser_page to locate it, then take a fresh snapshot.'
                        : ''
                }`,
                data
            );
        }
    }
};

const describeActTarget = (target: {
    ref: string;
    selector: string;
    text: string;
}): string =>
    target.ref
        ? `element ${target.ref}`
        : target.text
          ? `"${target.text}"`
          : target.selector
            ? `the element matching ${target.selector}`
            : '';

// Spoken confirmation of a successful action. Typed values are never read back
// (they may be sensitive); the model has them in its own arguments anyway.
const describeActSuccess = (
    action: VoiceActAction,
    target: string,
    key: string
): string => {
    const on = target ? ` ${target}` : '';
    switch (action) {
        case 'click':
            return `Clicked${on}.`;
        case 'double_click':
            return `Double-clicked${on}.`;
        case 'right_click':
            return `Right-clicked${on}.`;
        case 'hover':
            return `Hovering over${on || ' the element'}.`;
        case 'type':
            return `Typed into${on || ' the field'}.`;
        case 'press':
            return `Pressed ${key || 'the key'}${target ? ` on ${target}` : ''}.`;
        case 'clear':
            return `Cleared${on || ' the field'}.`;
        case 'select':
            return `Selected the option in${on || ' the list'}.`;
        case 'check':
            return `Checked${on}.`;
        case 'uncheck':
            return `Unchecked${on}.`;
        case 'focus':
            return `Focused${on}.`;
        case 'scroll':
            return `Scrolled${target ? ` ${target}` : ' the page'}.`;
        case 'scroll_into_view':
            return `Scrolled${on} into view.`;
        case 'wait_for':
            return 'The wait finished.';
        case 'describe':
            return 'Described the element; the details are in the result.';
    }
};

const useExtensionInfo = (): ExtensionInfo | null => {
    const [info, setInfo] = useState<ExtensionInfo | null>(getExtensionInfo());
    useEffect(() => {
        const unsubscribe = subscribeToExtensionInfo(setInfo);
        setInfo(getExtensionInfo());
        if (!getExtensionInfo()) {
            // The hello may have been sent before the side panel's listener was
            // attached; ask again once. An older build never answers.
            postEmbedReady();
        }
        return unsubscribe;
    }, []);
    return info;
};

const useActiveTabInfo = (): ActiveTabInfo | null => {
    const [info, setInfo] = useState<ActiveTabInfo | null>(getActiveTabInfo());
    useEffect(() => {
        const unsubscribe = subscribeToActiveTabInfo(setInfo);
        setInfo(getActiveTabInfo());
        return unsubscribe;
    }, []);
    return info;
};

const runRead = async (command: string): Promise<ReadOutcome> => {
    const reply: BrowserReadReply = await requestBrowserRead([{command}]);
    if (reply.type !== 'browser_interaction_result') {
        return {ok: false, documentReply: true, error: reply.error};
    }
    const entry = reply.results?.[0];
    if (!entry) {
        return {
            ok: Boolean(reply.ok),
            value: reply.result as BrowserReadValue | undefined,
            error: reply.error
        };
    }
    return {
        ok: entry.ok,
        value: entry.result as BrowserReadValue | undefined,
        error: entry.error
    };
};

const VoiceBrowserActions: React.FC = () => {
    const location = useLocation();
    const authenticatedFetch = useCreateAuthenticatedFetch();
    const extensionInfo = useExtensionInfo();
    // The latest element snapshot, handed to the vision model so it can name
    // the refs that match what it sees.
    const lastSnapshotRef = useRef<{text: string; tabId?: number} | null>(null);
    const activeTab = useActiveTabInfo();
    const capabilities = extensionInfo?.capabilities;
    const canNavigate = capabilities?.has('browser_navigate') ?? false;
    const canScroll = capabilities?.has('browser_scroll') ?? false;
    const canManageTabs = capabilities?.has('browser_tabs') ?? false;
    const canReadSelection =
        capabilities?.has('browser_read_selection') ?? false;
    // Snapshot / act / region screenshot ride `browser_action_request`, which
    // only newer builds understand; older ones never see these actions.
    const canAct = capabilities?.has(BROWSER_ACTION_CAPABILITY) ?? false;

    // callIds of voice actions awaiting the extension. A `browser_action_pending`
    // for one of them means the side panel is asking the user to Allow/Deny, so
    // the wait follows the prompt's own deadline instead of timing out first.
    const pendingActCallIdsRef = useRef(new Set<string>());
    useEffect(() => {
        if (!canAct) {
            return;
        }
        return subscribeToBrowserActionPending(({callId, expiresAt}) => {
            if (!pendingActCallIdsRef.current.has(callId)) {
                return;
            }
            extendExtensionRequestDeadline(
                callId,
                expiresAt + pendingConfirmationGraceMs
            );
        });
    }, [canAct]);
    // Capture and attach land in the composer, so they need an assistant chat
    // page (new thread or open thread), not the assistant's settings pages.
    const composerOpen = /^\/assistants\/[^/]+(\/threads\/[^/]+)?\/?$/.test(
        location.pathname
    );

    // Full page text cached per part so "continue" (offset) reads the same
    // snapshot rather than re-fetching a page that may have changed.
    const pageCacheRef = useRef<{part: string; text: string} | null>(null);
    const confirmationGateRef = useRef(createVoiceConfirmationGate());
    const pendingCloseRef = useRef<BrowserTabSummary | null>(null);

    useVoicePageFact(
        'browserShell',
        canAct
            ? 'Theta Assist is running in the browser side panel beside a web page. It can list the elements on that page with snapshot_browser_page and click, type into, and fill forms on it with act_on_browser_page; sensitive steps ask the user to allow them in the side panel.'
            : 'Theta Assist is running in the browser side panel beside a web page. It can read, scroll, and navigate the page but cannot click or type on it with this extension version.'
    );
    useVoicePageFact('browserTab', describeTab(activeTab));

    useVoiceAction(
        useMemo(
            () => ({
                id: 'get_browser_page_info',
                label: 'Browser Page Info',
                description:
                    'Report the title and site of the web page open in the browser tab beside the panel. Use for "what page am I on", "which site is this", or before reading when the tab is unknown.',
                parameters: {
                    type: 'object',
                    properties: {},
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(async () => {
            const cached = getActiveTabInfo();
            const cachedDescription = describeTab(cached);
            if (cached && cachedDescription) {
                return speak(cachedDescription, {
                    title: cached.title,
                    url: cached.url,
                    restricted: cached.restricted
                });
            }
            try {
                const reply = await requestBrowserRead([
                    {command: 'title'},
                    {command: 'url'}
                ]);
                if (reply.type !== 'browser_interaction_result') {
                    return speak(documentReplyMessage, {documentReply: true});
                }
                const [titleEntry, urlEntry] = reply.results ?? [];
                if (!titleEntry?.ok && !urlEntry?.ok) {
                    return unavailable(
                        describeReadError(titleEntry?.error ?? urlEntry?.error)
                    );
                }
                const title = readValueToText(
                    titleEntry?.result as BrowserReadValue | undefined
                );
                const url = readValueToText(
                    urlEntry?.result as BrowserReadValue | undefined
                );
                return speak(
                    title
                        ? `The browser tab shows "${title}" at ${hostnameOf(url)}.`
                        : `The browser tab shows ${hostnameOf(url)}.`,
                    {title, url}
                );
            } catch (error) {
                return mapExtensionError(error);
            }
        }, []),
        {global: true}
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'read_browser_page',
                label: 'Read Browser Page',
                description: `Read the web page open in the browser tab beside the panel, not the Theta Assist thread. Use part summary for "what is this page about", "summarise this page", or any question about the page, then answer in your own words from the returned text. Use part full only when the user asks to hear the page read out; it comes back in chunks, so pass the returned nextOffset as offset to continue. Use headings for "what sections are there", links for "what links are on this page"${
                    canReadSelection
                        ? ', and selection for text the user has highlighted in the web page'
                        : ''
                }.`,
                parameters: {
                    type: 'object',
                    properties: {
                        part: {
                            type: 'string',
                            enum: [
                                'summary',
                                'full',
                                'headings',
                                'links',
                                ...(canReadSelection ? ['selection'] : [])
                            ]
                        },
                        offset: {
                            type: 'integer',
                            description:
                                'For part full: character offset to continue reading from, taken from the previous result.'
                        }
                    },
                    required: ['part'],
                    additionalProperties: false
                }
            }),
            [canReadSelection]
        ),
        useCallback(async args => {
            const part = getStringArg(args, 'part') || 'summary';
            const offset = getIntegerArg(args, 'offset') ?? 0;

            try {
                if (part === 'headings') {
                    const outcome = await runRead(
                        "querySelectorAll 'h1, h2, h3' --visible --limit 40"
                    );
                    if (outcome.documentReply) {
                        return speak(documentReplyMessage, {
                            documentReply: true
                        });
                    }
                    if (!outcome.ok) {
                        return unavailable(describeReadError(outcome.error));
                    }
                    const headings = Array.from(
                        new Set(
                            (outcome.value?.elements ?? [])
                                .map(element => (element.text ?? '').trim())
                                .filter(Boolean)
                        )
                    );
                    if (headings.length === 0) {
                        return speak('The page has no visible headings.', {
                            headings: []
                        });
                    }
                    const spoken = headings.slice(0, spokenListLimit);
                    const remaining = headings.length - spoken.length;
                    return speak(
                        `The page has ${headings.length} ${headings.length === 1 ? 'heading' : 'headings'}: ${spoken
                            .map((heading, index) => `${index + 1}. ${heading}`)
                            .join(
                                '; '
                            )}.${remaining > 0 ? ` There are ${remaining} more.` : ''}`,
                        {headings}
                    );
                }

                if (part === 'links') {
                    const outcome = await runRead(
                        'listLinks --visible --limit 60'
                    );
                    if (outcome.documentReply) {
                        return speak(documentReplyMessage, {
                            documentReply: true
                        });
                    }
                    if (!outcome.ok) {
                        return unavailable(describeReadError(outcome.error));
                    }
                    const seen = new Set<string>();
                    const links = (outcome.value?.elements ?? [])
                        .map(element => ({
                            text: (element.text ?? '').trim(),
                            href: element.href ?? ''
                        }))
                        .filter(link => {
                            if (
                                !link.text ||
                                !link.href ||
                                seen.has(link.href)
                            ) {
                                return false;
                            }
                            seen.add(link.href);
                            return true;
                        })
                        .slice(0, 30);
                    if (links.length === 0) {
                        return speak(
                            'The page has no visible links with text.',
                            {
                                links: []
                            }
                        );
                    }
                    const spoken = links.slice(0, spokenListLimit);
                    const remaining = links.length - spoken.length;
                    return speak(
                        `Links on the page: ${spoken
                            .map((link, index) => `${index + 1}. ${link.text}`)
                            .join(
                                '; '
                            )}.${remaining > 0 ? ` There are ${remaining} more.` : ''}`,
                        {links}
                    );
                }

                if (part === 'selection') {
                    const outcome = await runRead('selection');
                    if (outcome.documentReply) {
                        return speak(documentReplyMessage, {
                            documentReply: true
                        });
                    }
                    if (!outcome.ok) {
                        return unavailable(describeReadError(outcome.error));
                    }
                    const selected = readValueToText(outcome.value);
                    if (!selected) {
                        return speak('Nothing is selected on the web page.', {
                            selectedText: ''
                        });
                    }
                    const slice = sliceSpokenText(selected, 0, 4_000);
                    return {
                        status: 'ready' as const,
                        message: 'Selected text from the web page captured.',
                        data: {selectedText: slice.text, ...slice}
                    };
                }

                // summary / full: the readable article text, falling back to the
                // whole body text when the page has no extractable article.
                let text = '';
                const cache = pageCacheRef.current;
                if (part === 'full' && offset > 0 && cache?.part === 'full') {
                    text = cache.text;
                } else {
                    const readable = await runRead('readable');
                    if (readable.documentReply) {
                        return speak(documentReplyMessage, {
                            documentReply: true
                        });
                    }
                    if (!readable.ok) {
                        return unavailable(describeReadError(readable.error));
                    }
                    text = readValueToText(readable.value);
                    if (!text) {
                        const plain = await runRead('text');
                        if (plain.ok) {
                            text = readValueToText(plain.value);
                        }
                    }
                    pageCacheRef.current = {part, text};
                }

                if (!text) {
                    return speak(
                        'The page has no readable text. It may be an image, a document viewer, or still loading.',
                        {text: ''}
                    );
                }

                if (part === 'full') {
                    const slice = sliceSpokenText(text, offset);
                    return {
                        status: 'ready' as const,
                        message: slice.truncated
                            ? 'Reading part of the page. Offer to continue with the returned nextOffset.'
                            : 'Reading the rest of the page.',
                        speakText: slice.text,
                        data: slice
                    };
                }

                const slice = sliceSpokenText(text, 0, summaryCharacterBudget);
                return {
                    status: 'ready' as const,
                    message:
                        'Page text captured. Answer the user from it in your own words; do not read it out.',
                    data: {
                        title: getActiveTabInfo()?.title,
                        url: getActiveTabInfo()?.url,
                        text: slice.text,
                        truncated: slice.truncated,
                        totalCharacters: slice.totalCharacters
                    }
                };
            } catch (error) {
                return mapExtensionError(error);
            }
        }, []),
        {
            global: true,
            activity: {label: 'Reading the page', icon: 'page'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'find_on_browser_page',
                label: 'Find On Browser Page',
                description:
                    'Search the web page in the browser tab for a word or phrase and report the matches with surrounding text. Use for "find X on this page", "does this page mention X", or "where does it say X".',
                parameters: {
                    type: 'object',
                    properties: {
                        text: {
                            type: 'string',
                            description: 'The word or phrase to look for.'
                        },
                        limit: {
                            type: 'integer',
                            description:
                                'Maximum matches to return (default 10, at most 25).'
                        }
                    },
                    required: ['text'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(async args => {
            const needle = sanitiseSearchText(getStringArg(args, 'text'));
            if (!needle) {
                return speak('Tell me what to look for on the page.');
            }
            const requested = getIntegerArg(args, 'limit');
            const limit = Math.min(Math.max(requested ?? 10, 1), 25);
            try {
                const outcome = await runRead(
                    `findText "${needle}" --context 160 --visible --limit ${limit}`
                );
                if (outcome.documentReply) {
                    return speak(documentReplyMessage, {documentReply: true});
                }
                if (!outcome.ok) {
                    return unavailable(describeReadError(outcome.error));
                }
                const data = outcome.value?.data as
                    | {matches?: FindMatch[]; truncated?: boolean}
                    | undefined;
                const matches = (data?.matches ?? [])
                    .map(match =>
                        (match.text ?? '').replace(/\s+/g, ' ').trim()
                    )
                    .filter(Boolean);
                if (matches.length === 0) {
                    return speak(`I could not find "${needle}" on the page.`, {
                        needle,
                        matches: []
                    });
                }
                const spoken = matches.slice(0, 3);
                return speak(
                    `Found ${matches.length}${data?.truncated ? ' or more' : ''} ${matches.length === 1 ? 'match' : 'matches'} for "${needle}". ${spoken
                        .map(
                            (snippet, index) =>
                                `${index + 1}. ${snippet.length > 220 ? `${snippet.slice(0, 220)}...` : snippet}`
                        )
                        .join(' ')}`,
                    {needle, matches, truncated: data?.truncated === true}
                );
            } catch (error) {
                return mapExtensionError(error);
            }
        }, []),
        {
            global: true,
            activity: {label: 'Searching the page', icon: 'page'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'capture_browser_screenshot',
                label: 'Screenshot Browser Page',
                description:
                    'Take a screenshot of the web page in the browser tab and attach it to the message box of the open assistant. It is not sent: the user can then dictate a question and say send. Set full true only when the user asks for the whole page, which takes longer. To capture one element or region, give its ref from snapshot_browser_page in region_ref, or a CSS selector in region_selector.',
                parameters: {
                    type: 'object',
                    properties: {
                        full: {
                            type: 'boolean',
                            description:
                                'True to capture the entire scrollable page rather than the visible part.'
                        },
                        region_ref: {
                            type: 'string',
                            description:
                                'Ref of the element to crop to, from an earlier snapshot, for example e12 or f2e4.'
                        },
                        region_selector: {
                            type: 'string',
                            description:
                                'CSS selector of the element to crop to, when no ref is known.'
                        }
                    },
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(
            async args => {
                const full = args.full === true;
                const regionRef = getStringArg(args, 'region_ref');
                const regionSelector = getStringArg(args, 'region_selector');
                if ((regionRef || regionSelector) && canAct) {
                    // Cropped capture: answered on the claimed callId, so the
                    // composer's own subscription skips it and we hand the file over.
                    try {
                        const reply = await requestBrowserAction(
                            {
                                action: 'screenshot',
                                params: {
                                    region: {
                                        ref: regionRef || undefined,
                                        selector: regionSelector || undefined
                                    }
                                }
                            },
                            {timeoutMs: voiceRegionScreenshotTimeoutMs}
                        );
                        if (reply.type !== 'screenshot_result') {
                            return reply.ok
                                ? unavailable(
                                      'The browser did not return an image for that region.',
                                      {regionRef, regionSelector}
                                  )
                                : describeBrowserActionFailure(
                                      reply,
                                      'The screenshot failed'
                                  );
                        }
                        if (!reply.ok || !reply.dataUrl) {
                            return unavailable(
                                `The screenshot failed${reply.error ? `: ${reply.error}` : ''}.`,
                                {regionRef, regionSelector}
                            );
                        }
                        const file = await dataUrlToFile(
                            reply.dataUrl,
                            reply.fileName || 'screenshot.png'
                        );
                        dispatchCustomAttachComposerFiles([file], reply.notice);
                        await waitForUiSurfaceToSettle();
                        return speak(
                            'Screenshot of that part of the page attached to your message. Say send when you are ready, or dictate a question first.',
                            {
                                fileName: file.name,
                                attached: true,
                                region: reply.region
                            }
                        );
                    } catch (error) {
                        return mapExtensionError(error);
                    }
                }
                const regionFallback =
                    regionRef || regionSelector
                        ? ' This extension version cannot crop to an element, so the visible page was captured instead.'
                        : '';
                try {
                    const waiting = waitForComposerAttachment(
                        ['screenshot_result'],
                        full ? 90_000 : 20_000
                    );
                    postCaptureRequest(full);
                    const outcome = await waiting;
                    await waitForUiSurfaceToSettle();
                    if (!outcome.ok) {
                        return unavailable(
                            `The screenshot failed${outcome.error ? `: ${outcome.error}` : ''}.`,
                            {full}
                        );
                    }
                    return speak(
                        `${full ? 'Full-page screenshot' : 'Screenshot'} attached to your message. Say send when you are ready, or dictate a question first.${regionFallback}`,
                        {full, fileName: outcome.fileName, attached: true}
                    );
                } catch (error) {
                    return mapExtensionError(error);
                }
            },
            [canAct]
        ),
        {
            global: true,
            enabled: composerOpen,
            activity: {label: 'Taking a screenshot', icon: 'look'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'attach_browser_page_content',
                label: 'Attach Browser Page',
                description:
                    'Convert the web page in the browser tab to a text document and attach it to the message box of the open assistant, so the assistant can read the whole page. It is not sent: the user can then dictate a question and say send. Use for "attach this page", "send this page to the assistant", or when a page is too long to read aloud.',
                parameters: {
                    type: 'object',
                    properties: {},
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(async () => {
            try {
                // A SharePoint document arrives as a file, and an encrypted one
                // as a screenshot fallback; the composer attaches all three.
                const waiting = waitForComposerAttachment(
                    ['markdown_result', 'file_result', 'screenshot_result'],
                    30_000
                );
                postMarkdownRequest();
                const outcome = await waiting;
                await waitForUiSurfaceToSettle();
                if (!outcome.ok) {
                    return unavailable(
                        `The page could not be attached${outcome.error ? `: ${outcome.error}` : ''}.`
                    );
                }
                const what =
                    outcome.type === 'file_result'
                        ? 'The document'
                        : outcome.type === 'screenshot_result'
                          ? 'A screenshot of the document'
                          : 'The page';
                return speak(
                    `${what} is attached to your message. Say send when you are ready, or dictate a question first.`,
                    {
                        fileName: outcome.fileName,
                        attached: true,
                        kind: outcome.type
                    }
                );
            } catch (error) {
                return mapExtensionError(error);
            }
        }, []),
        {
            global: true,
            enabled: composerOpen,
            activity: {label: 'Attaching the page', icon: 'page'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'navigate_browser',
                label: 'Navigate Browser',
                description:
                    'Change the web page in the browser tab: open a web address, go back, go forward, or reload. Use when "go to" is followed by a website, domain, or address, and for "go back", "forward", or "refresh the page". Not for Theta Assist pages such as Home or Library.',
                parameters: {
                    type: 'object',
                    properties: {
                        action: {
                            type: 'string',
                            enum: ['url', 'back', 'forward', 'reload']
                        },
                        url: {
                            type: 'string',
                            description:
                                'For action url: the web address or domain to open, for example bbc.com or https://example.com/page.'
                        }
                    },
                    required: ['action'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(
            async args => {
                const action = getStringArg(args, 'action') || 'url';
                if (!['url', 'back', 'forward', 'reload'].includes(action)) {
                    return speak(
                        'Navigation must be url, back, forward, or reload.'
                    );
                }
                let url: string | undefined;
                if (action === 'url') {
                    const normalised = normaliseUrl(getStringArg(args, 'url'));
                    if (!normalised) {
                        return speak(
                            'Please say the full web address, for example example dot com.'
                        );
                    }
                    url = normalised;
                }

                if (!canNavigate) {
                    if (action === 'url' && url) {
                        // Older extension: fire-and-forget navigation only.
                        postNavigateRequest(url);
                        return speak(
                            `Asked the browser to open ${hostnameOf(url)}. I cannot tell when it has finished loading with this version of the extension.`,
                            {url, settled: false}
                        );
                    }
                    return unavailable(updateNeededMessage, {action});
                }

                try {
                    const reply = await requestBrowserNavigate(
                        action as 'url' | 'back' | 'forward' | 'reload',
                        url
                    );
                    if (!reply.ok) {
                        return unavailable(
                            reply.error ?? 'The browser could not navigate.',
                            {action, url}
                        );
                    }
                    const where = reply.title?.trim()
                        ? `"${reply.title.trim()}"`
                        : hostnameOf(reply.url ?? url);
                    const loading = reply.settled
                        ? ''
                        : ' The page is still loading.';
                    const verb =
                        action === 'back'
                            ? 'Went back to'
                            : action === 'forward'
                              ? 'Went forward to'
                              : action === 'reload'
                                ? 'Reloaded'
                                : 'Opened';
                    return speak(`${verb} ${where}.${loading}`, {
                        action,
                        url: reply.url,
                        title: reply.title,
                        settled: reply.settled
                    });
                } catch (error) {
                    return mapExtensionError(error);
                }
            },
            [canNavigate]
        ),
        {
            global: true,
            activity: {label: 'Opening the page', icon: 'navigate'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'scroll_browser_page',
                label: 'Scroll Browser Page',
                description:
                    'Scroll the web page in the browser tab: down or up by screens, to the top or bottom, or to the first place a passage of text appears. Use for "scroll down", "page up", "go to the top", or "scroll to the section about X".',
                parameters: {
                    type: 'object',
                    properties: {
                        to: {
                            type: 'string',
                            enum: ['down', 'up', 'top', 'bottom', 'text']
                        },
                        amount: {
                            type: 'integer',
                            description:
                                'For down or up: how many screens to move (default 1).'
                        },
                        text: {
                            type: 'string',
                            description:
                                'For to text: the passage to bring into view.'
                        }
                    },
                    required: ['to'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(async args => {
            const to = getStringArg(args, 'to') || 'down';
            if (!['down', 'up', 'top', 'bottom', 'text'].includes(to)) {
                return speak('Scroll must be down, up, top, bottom, or text.');
            }
            const amount = getIntegerArg(args, 'amount');
            const text = sanitiseSearchText(getStringArg(args, 'text'));
            if (to === 'text' && !text) {
                return speak('Tell me which text to scroll to.');
            }
            try {
                const reply = await requestBrowserScroll(
                    to as 'down' | 'up' | 'top' | 'bottom' | 'text',
                    amount && amount > 0 ? amount : undefined,
                    to === 'text' ? text : undefined
                );
                if (!reply.ok) {
                    return unavailable(
                        reply.error &&
                            /cannot scroll this page/i.test(reply.error)
                            ? 'This tab is a browser page I cannot scroll. Switch to a normal web page and ask again.'
                            : `The page could not be scrolled${reply.error ? `: ${reply.error}` : ''}.`,
                        {to}
                    );
                }
                const position = {
                    percent: reply.percent,
                    atTop: reply.atTop,
                    atBottom: reply.atBottom,
                    foundText: reply.foundText
                };
                if (to === 'text') {
                    return speak(
                        reply.foundText
                            ? `Scrolled to "${text}".`
                            : `I could not find "${text}" on the page.`,
                        position
                    );
                }
                if (to === 'top' || reply.atTop) {
                    return speak('At the top of the page.', position);
                }
                if (to === 'bottom' || reply.atBottom) {
                    return speak('At the bottom of the page.', position);
                }
                return speak(
                    `Scrolled ${to}. About ${reply.percent ?? 0} percent of the way through the page.`,
                    position
                );
            } catch (error) {
                return mapExtensionError(error);
            }
        }, []),
        {
            global: true,
            enabled: canScroll,
            activity: {label: 'Scrolling the page', icon: 'page'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'manage_browser_tabs',
                label: 'Browser Tabs',
                description:
                    'List the browser tabs in this window, switch to one, open a web address in a new tab, or close one. Identify a tab by part of its title or address in match, or by tabId from an earlier list. Closing asks a confirmation question first; call again with confirm true only after the user says yes.',
                parameters: {
                    type: 'object',
                    properties: {
                        action: {
                            type: 'string',
                            enum: ['list', 'switch', 'open', 'close']
                        },
                        match: {
                            type: 'string',
                            description:
                                'Part of the title or address of the tab to switch to or close.'
                        },
                        tabId: {
                            type: 'integer',
                            description:
                                'Exact tab id from an earlier list or candidates result.'
                        },
                        url: {
                            type: 'string',
                            description:
                                'For action open: the web address or domain to open in a new tab.'
                        },
                        confirm: confirmParameter
                    },
                    required: ['action'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(async args => {
            const action = getStringArg(args, 'action') || 'list';
            const match = getStringArg(args, 'match');
            const tabId = getIntegerArg(args, 'tabId');
            const gate = confirmationGateRef.current;

            const describeCandidates = (candidates: BrowserTabSummary[]) =>
                speak(
                    `Several tabs match: ${candidates
                        .slice(0, spokenListLimit)
                        .map(
                            (tab, index) =>
                                `${index + 1}. ${tab.title || hostnameOf(tab.url)}`
                        )
                        .join('; ')}. Which one do you mean?`,
                    {status: 'ambiguous', candidates}
                );

            try {
                if (action === 'list') {
                    const reply = await requestBrowserTabs('list');
                    if (!reply.ok) {
                        return unavailable(
                            reply.error ?? 'The tabs could not be listed.'
                        );
                    }
                    const tabs = reply.tabs ?? [];
                    if (tabs.length === 0) {
                        return speak('There are no open tabs.', {tabs});
                    }
                    const spoken = tabs.slice(0, spokenListLimit);
                    const remaining = tabs.length - spoken.length;
                    return speak(
                        `There ${tabs.length === 1 ? 'is 1 tab' : `are ${tabs.length} tabs`} open: ${spoken
                            .map(
                                (tab, index) =>
                                    `${index + 1}. ${tab.title || hostnameOf(tab.url)}${tab.active ? ' (current)' : ''}`
                            )
                            .join(
                                '; '
                            )}.${remaining > 0 ? ` There are ${remaining} more.` : ''}`,
                        {tabs, activeTabId: reply.activeTabId}
                    );
                }

                if (action === 'switch') {
                    if (!match && tabId === undefined) {
                        return speak(
                            'Which tab? Say part of its title or address.'
                        );
                    }
                    const reply = await requestBrowserTabs('switch', {
                        match: match || undefined,
                        tabId
                    });
                    if (reply.candidates && reply.candidates.length > 0) {
                        return describeCandidates(reply.candidates);
                    }
                    if (!reply.ok || !reply.tab) {
                        return unavailable(
                            reply.error ?? 'The tab could not be switched to.'
                        );
                    }
                    return speak(
                        `Switched to "${reply.tab.title || hostnameOf(reply.tab.url)}".`,
                        {tab: reply.tab}
                    );
                }

                if (action === 'open') {
                    const url = normaliseUrl(getStringArg(args, 'url'));
                    if (!url) {
                        return speak(
                            'Please say the full web address to open in a new tab.'
                        );
                    }
                    const reply = await requestBrowserTabs('open', {url});
                    if (!reply.ok) {
                        return unavailable(
                            reply.error ?? 'The tab could not be opened.',
                            {url}
                        );
                    }
                    return speak(
                        `Opened ${reply.tab?.title ? `"${reply.tab.title}"` : hostnameOf(url)} in a new tab.`,
                        {tab: reply.tab}
                    );
                }

                if (action === 'close') {
                    const pending = pendingCloseRef.current;
                    if (pending && gate.isConfirmed('close_tab', args)) {
                        pendingCloseRef.current = null;
                        const reply = await requestBrowserTabs('close', {
                            tabId: pending.tabId
                        });
                        if (!reply.ok) {
                            return unavailable(
                                reply.error ?? 'The tab could not be closed.'
                            );
                        }
                        return speak(
                            `Closed "${pending.title || hostnameOf(pending.url)}".`,
                            {tab: pending}
                        );
                    }
                    if (!match && tabId === undefined) {
                        return speak(
                            'Which tab should I close? Say part of its title or address.'
                        );
                    }
                    // Resolve the tab first so the confirmation names it.
                    const listing = await requestBrowserTabs('list');
                    if (!listing.ok) {
                        return unavailable(
                            listing.error ?? 'The tabs could not be listed.'
                        );
                    }
                    const tabs = listing.tabs ?? [];
                    const needle = match.toLowerCase();
                    let hits =
                        tabId !== undefined
                            ? tabs.filter(tab => tab.tabId === tabId)
                            : tabs.filter(tab =>
                                  `${tab.title} ${tab.url}`
                                      .toLowerCase()
                                      .includes(needle)
                              );
                    if (hits.length > 1) {
                        const titleHits = hits.filter(tab =>
                            tab.title.toLowerCase().includes(needle)
                        );
                        if (titleHits.length === 1) {
                            hits = titleHits;
                        }
                    }
                    if (hits.length === 0) {
                        return speak(
                            tabId !== undefined
                                ? 'That tab is no longer open.'
                                : `No open tab matches "${match}".`
                        );
                    }
                    if (hits.length > 1) {
                        return describeCandidates(hits);
                    }
                    pendingCloseRef.current = hits[0];
                    return gate.prompt(
                        'close_tab',
                        `Close the tab "${hits[0].title || hostnameOf(hits[0].url)}"? Say yes to confirm.`
                    );
                }

                return speak(
                    'Tab action must be list, switch, open, or close.'
                );
            } catch (error) {
                return mapExtensionError(error);
            }
        }, []),
        {
            global: true,
            enabled: canManageTabs,
            activity: {label: 'Switching tabs', icon: 'tabs'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'snapshot_browser_page',
                label: 'Snapshot Browser Page',
                description:
                    'List the elements of the web page in the browser tab (buttons, links, fields, menus, headings) with a ref such as e12 for each, so you can act on them with act_on_browser_page. Take a snapshot before acting, and take a new one after the page changes: refs from an old snapshot stop working. Do not read the list aloud; summarise what is on the page and use the refs. Narrow with filter (text to match) or scope (a CSS selector) when the page is large. Table and grid cells are listed with row= and col= headers, and a filter keeps the whole row of any matching cell. Icon-only controls appear as clickable with class= and onclick= hints instead of a name.',
                parameters: {
                    type: 'object',
                    properties: {
                        filter: {
                            type: 'string',
                            description:
                                'Only list elements whose name or text contains this.'
                        },
                        scope: {
                            type: 'string',
                            description:
                                'CSS selector of the region to list, for example form or main.'
                        },
                        frame: {
                            type: 'string',
                            description:
                                'Frame id from an earlier snapshot, for example f2, to list only that frame.'
                        },
                        interactive_only: {
                            type: 'boolean',
                            description:
                                'False to include headings and static text as well as interactive elements. Default true.'
                        }
                    },
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(async args => {
            const filter = getStringArg(args, 'filter');
            const scope = getStringArg(args, 'scope');
            const frame = getStringArg(args, 'frame');
            const interactiveOnly = args.interactive_only !== false;
            try {
                const reply = await requestBrowserAction(
                    {
                        action: 'snapshot',
                        params: {
                            filter: filter || undefined,
                            scope: scope || undefined,
                            frame: frame || undefined,
                            interactiveOnly,
                            maxLines: voiceSnapshotMaxLines
                        }
                    },
                    {timeoutMs: voiceSnapshotTimeoutMs}
                );
                if (reply.type !== 'browser_action_result') {
                    return unavailable(
                        'The browser returned an unexpected reply to the snapshot.'
                    );
                }
                if (!reply.ok) {
                    return describeBrowserActionFailure(
                        reply,
                        'The page could not be listed'
                    );
                }
                const text = snapshotText(reply.result);
                lastSnapshotRef.current = {text, tabId: reply.tabId};
                const truncated = text.length > voiceSnapshotCharacterBudget;
                const count = countSnapshotElements(text);
                return speak(
                    count > 0
                        ? `Found ${count} element${count === 1 ? '' : 's'} on the page.`
                        : 'I could not find any elements on the page to act on.',
                    {
                        text: truncated
                            ? text.slice(0, voiceSnapshotCharacterBudget)
                            : text,
                        truncated,
                        elementCount: count,
                        tabId: reply.tabId,
                        url: reply.url,
                        title: reply.title
                    }
                );
            } catch (error) {
                return mapExtensionError(error);
            }
        }, []),
        {
            global: true,
            enabled: canAct,
            activity: {label: 'Mapping the page', icon: 'page'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'look_at_browser_page',
                label: 'Look At Browser Page',
                description:
                    'Have a vision model look at a screenshot of the web page and answer a question about it, in words you can act on: what an icon or picture shows, where something sits, which snapshot ref an element the user points at corresponds to. Use it only when the snapshot cannot answer: the user describes something by appearance or position, asks about a picture, chart, or layout, the snapshot is ambiguous or icon-only, or an action could not find its target. Ask one specific question. Pass region_ref (or region_selector) for the row, panel, or dialog you mean when you know it, and full true only for the whole scrollable page. It captures the tab the browser session is working in, including a pop-up window it opened; pass tab_id from manage_browser_tabs to look at another tab. The result gives a short answer in speakText and, in message, what is on the screen line by line, such as each field with its value. Speak the answer, then use the detail to answer follow-up questions and to act, instead of looking again or reading the whole list aloud. The image is not kept and is not added to the conversation.',
                parameters: {
                    type: 'object',
                    properties: {
                        question: {
                            type: 'string',
                            description:
                                'What to find out from the screenshot, for example "Which icon edits the Monday row?" or "What does the chart show?".'
                        },
                        region_ref: {
                            type: 'string',
                            description:
                                'Snapshot ref of the element to crop to, for example the table row or dialog.'
                        },
                        region_selector: {
                            type: 'string',
                            description:
                                'CSS selector of the region to crop to, when no ref is known.'
                        },
                        full: {
                            type: 'boolean',
                            description:
                                'True to capture the whole scrollable page instead of the visible part.'
                        },
                        tab_id: {
                            type: 'integer',
                            description:
                                'Tab to look at, from manage_browser_tabs. Omit for the tab the session is working in.'
                        }
                    },
                    required: ['question'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(
            async args => {
                const question =
                    getStringArg(args, 'question') ||
                    'Describe what is shown on the page.';
                const full = args.full === true;
                const regionRef = getStringArg(args, 'region_ref');
                const regionSelector = getStringArg(args, 'region_selector');
                const tabId = getIntegerArg(args, 'tab_id');
                const hasRegion = Boolean(regionRef || regionSelector);

                try {
                    let dataUrl: string | undefined;
                    let isRegion = false;
                    let capturedTabId: number | undefined;

                    if (canAct) {
                        // The page-action route follows the session's working
                        // tab (a pop-up the assistant opened included).
                        const reply = await requestBrowserAction(
                            {
                                action: 'screenshot',
                                params: {
                                    full: full && !hasRegion ? true : undefined,
                                    region: hasRegion
                                        ? {
                                              ref: regionRef || undefined,
                                              selector:
                                                  regionSelector || undefined
                                          }
                                        : undefined
                                },
                                tabId
                            },
                            {timeoutMs: voiceLookTimeoutMs}
                        );
                        if (reply.type !== 'screenshot_result') {
                            return reply.ok
                                ? unavailable(
                                      'The browser did not return an image to look at.'
                                  )
                                : describeBrowserActionFailure(
                                      reply,
                                      'The screenshot failed'
                                  );
                        }
                        if (!reply.ok || typeof reply.dataUrl !== 'string') {
                            return unavailable(
                                `The screenshot failed${reply.error ? `: ${reply.error}` : ''}.`
                            );
                        }
                        dataUrl = reply.dataUrl;
                        isRegion = hasRegion;
                        capturedTabId =
                            typeof reply.tabId === 'number'
                                ? reply.tabId
                                : tabId;
                    } else {
                        // Older extension: plain capture of the active tab only.
                        const reply = await requestBrowserRead(
                            [
                                {
                                    command: full
                                        ? 'screenshot --full'
                                        : 'screenshot'
                                }
                            ],
                            full ? 90_000 : 20_000
                        );
                        const shot = reply as {
                            type: string;
                            ok?: boolean;
                            dataUrl?: unknown;
                            error?: string;
                        };
                        if (shot.type !== 'screenshot_result') {
                            return unavailable(
                                'The browser did not return an image to look at.'
                            );
                        }
                        if (!shot.ok || typeof shot.dataUrl !== 'string') {
                            return unavailable(
                                `The screenshot failed${shot.error ? `: ${shot.error}` : ''}.`
                            );
                        }
                        dataUrl = shot.dataUrl;
                    }

                    const image = await downscaleImageDataUrl(dataUrl);
                    const tab = getActiveTabInfo();
                    const snapshot = lastSnapshotRef.current;
                    const snapshotMatchesTab =
                        snapshot &&
                        (capturedTabId === undefined ||
                            snapshot.tabId === undefined ||
                            snapshot.tabId === capturedTabId);
                    const result = await describeScreenshot(
                        authenticatedFetch,
                        {
                            imageDataUrl: image,
                            question,
                            snapshotText:
                                snapshotMatchesTab && snapshot
                                    ? snapshot.text
                                    : undefined,
                            pageTitle:
                                capturedTabId === undefined
                                    ? tab?.title
                                    : undefined,
                            pageUrl:
                                capturedTabId === undefined
                                    ? tab?.url
                                    : undefined,
                            isRegion
                        }
                    );
                    // The panel logged the capture but cannot see what the
                    // vision model made of it, so the answer goes to its
                    // diagnostics log too.
                    postVoiceActivityNote({
                        command: `voice look${isRegion ? ' (region)' : full ? ' (full page)' : ''}: ${question}`,
                        ok: true,
                        detail: result.description,
                        model: result.model,
                        tabId: capturedTabId
                    });
                    // The detail behind the answer is for the model to use, not
                    // to read out, so only the answer goes in speakText.
                    return {
                        status: 'ready',
                        message: result.description,
                        speakText: result.spokenAnswer,
                        data: {
                            question,
                            region: isRegion,
                            full,
                            tabId: capturedTabId,
                            model: result.model
                        }
                    };
                } catch (error) {
                    postVoiceActivityNote({
                        command: `voice look: ${question}`,
                        ok: false,
                        error: unknownErrorToString(error)
                    });
                    if (error instanceof ExtensionRequestTimeoutError) {
                        return mapExtensionError(error);
                    }
                    return unavailable(
                        `I could not get a description of the page: ${unknownErrorToString(error)}`,
                        {question}
                    );
                }
            },
            [authenticatedFetch, canAct]
        ),
        {
            global: true,
            activity: {label: 'Looking at the page', icon: 'look'}
        }
    );

    useVoiceAction(
        useMemo(
            () => ({
                id: 'act_on_browser_page',
                label: 'Act On Browser Page',
                description:
                    'Perform one action on an element of the web page in the browser tab: click, type, press a key, select an option, check a box, hover, focus, scroll, wait, or describe an element. Take snapshot_browser_page first and target the element by its ref; use its visible text only when no snapshot is available. One action per call; take a new snapshot after the page changes. In tables and grids, when the user names a row and a column, act on the cell at their intersection (match its row= and col= headers), not on the row label. Check the changes listed in the result: if the expected dialog or change did not happen, say so instead of reporting success. Never enter passwords, card numbers, or other secrets, and never click send, pay, submit, delete, or similar buttons, unless the user has explicitly asked for that exact step in this conversation; the side panel will also ask the user to allow such steps, and a denied result means stop and ask.',
                parameters: {
                    type: 'object',
                    properties: {
                        action: {
                            type: 'string',
                            enum: [...voiceActActions]
                        },
                        ref: {
                            type: 'string',
                            description:
                                'Element ref from the latest snapshot, for example e12 or f2e4. Preferred.'
                        },
                        selector: {
                            type: 'string',
                            description:
                                'CSS selector of the element, when no ref is known.'
                        },
                        text: {
                            type: 'string',
                            description:
                                'Visible text of the element (a button label or link text), when no ref is known.'
                        },
                        value: {
                            type: 'string',
                            description:
                                'For type: the text to enter. For select: the option to choose. For wait_for: the text or selector to wait for. For scroll: up, down, top, or bottom.'
                        },
                        key: {
                            type: 'string',
                            description:
                                'For press: the key or shortcut, for example Enter, Escape, Tab, or Control+a.'
                        },
                        submit: {
                            type: 'boolean',
                            description:
                                'For type: press Enter afterwards to submit the field.'
                        },
                        reason: {
                            type: 'string',
                            description:
                                'One short sentence saying why this step is needed; shown to the user.'
                        }
                    },
                    required: ['action', 'reason'],
                    additionalProperties: false
                }
            }),
            []
        ),
        useCallback(async args => {
            const action = getStringArg(args, 'action') as VoiceActAction;
            if (!voiceActActions.includes(action)) {
                return speak(
                    `The action must be one of ${voiceActActions.join(', ')}.`
                );
            }
            const target = {
                ref: getStringArg(args, 'ref'),
                selector: getStringArg(args, 'selector'),
                text: getStringArg(args, 'text')
            };
            const hasTarget = Boolean(
                target.ref || target.selector || target.text
            );
            if (!hasTarget && !targetOptionalActions.has(action)) {
                return speak(
                    'Say which element: give its ref from the snapshot, or its visible text.'
                );
            }
            const value = getStringArg(args, 'value');
            const key = getStringArg(args, 'key');
            const reason = getStringArg(args, 'reason');
            if (action === 'press' && !key) {
                return speak('Say which key to press, for example Enter.');
            }
            if ((action === 'type' || action === 'select') && !value) {
                return speak(
                    action === 'type'
                        ? 'Say what to type into the field.'
                        : 'Say which option to select.'
                );
            }

            const callId = generateVoiceCallId('voice-act');
            pendingActCallIdsRef.current.add(callId);
            try {
                const reply = await requestBrowserAction(
                    {
                        action: action as BrowserActionName,
                        target: hasTarget
                            ? {
                                  ref: target.ref || undefined,
                                  selector: target.selector || undefined,
                                  text: target.text || undefined
                              }
                            : undefined,
                        params: {
                            value: value || undefined,
                            key: key || undefined,
                            submit: args.submit === true ? true : undefined,
                            reason: reason || undefined
                        },
                        deadlineAt: Date.now() + voiceActTimeoutMs
                    },
                    {timeoutMs: voiceActTimeoutMs, callId}
                );
                if (reply.type !== 'browser_action_result') {
                    return unavailable(
                        'The browser returned an unexpected reply to the action.'
                    );
                }
                if (!reply.ok) {
                    return describeBrowserActionFailure(
                        reply,
                        'The action failed'
                    );
                }
                const result = isRecord(reply.result) ? reply.result : {};
                const changes = Array.isArray(result.changes)
                    ? result.changes
                          .filter(
                              (change): change is string =>
                                  typeof change === 'string'
                          )
                          .slice(0, 3)
                    : [];
                let message = describeActSuccess(
                    action,
                    describeActTarget(target),
                    key
                );
                if (changes.length) {
                    message += ` The page changed: ${changes.join('; ')}.`;
                }
                if (result.urlChanged === true) {
                    message +=
                        ' The page navigated, so take a new snapshot before the next action.';
                }
                return speak(message, {
                    action,
                    status: reply.status,
                    result: reply.result,
                    tabId: reply.tabId,
                    url: reply.url,
                    title: reply.title
                });
            } catch (error) {
                return mapExtensionError(error);
            } finally {
                pendingActCallIdsRef.current.delete(callId);
            }
        }, []),
        {
            global: true,
            enabled: canAct,
            activity: {label: 'Working on the page', icon: 'act'}
        }
    );

    return null;
};

export default VoiceBrowserActions;
